// /loans (D40, D46-D49): one row per borrow — what it was worth when borrowed, what it is worth now, what repaying
// costs in difference, and (D46) which repays are linked into it with the realized result. Repays no borrow is
// linked to yet wait for an assignment (D47). Works without a deal.
import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { getLoanList, getLocationItems, postRecomputePriceLevel, putPriceLevel } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { EventRow, LoanLine, LoanQuery, PrehistoryGroupView, PrehistoryRepayView, UnassignedGroupView, UnassignedRepayView } from '../api/model';
import { Async, ErrorNotice, Notice } from '../components/Async';
import { CardHead } from '../components/Card';
import { Dialog } from '../components/Dialog';
import { PREHISTORY_TITLE, PrehistoryMarkDialog, PrehistoryNoteDialog, PrehistorySection, PrehistoryUnmarkDialog, loadPrehistoryOpen, storePrehistoryOpen } from '../components/Prehistory';
import { PriceSourceLabel } from '../components/PriceSource';
import { RepayPanel } from '../components/RepayPanel';
import { HelpLabel } from '../components/Help';
import { Table, type Column } from '../components/Table';
import { Dash, Val, signClass } from '../components/Val';
import { reasonText } from '../i18n/reasons.hu';
import { addDecimal, compareDecimal, formatDecimal, formatPercent, isDecimalString, isZeroDecimal } from '../lib/decimal';
import { Link } from '../lib/router';
import { formatUtc } from '../lib/time';

/** Sum of a USD column over the shown rows; null + the first reason as soon as one row is unknown (never a partial total). */
function total(rows: LoanLine[], pick: (l: LoanLine) => { v: string | null; r: string | null }): { value: string | null; reason: string | null } {
  let sum = '0';
  for (const l of rows) {
    const { v, r } = pick(l);
    if (v === null) return { value: null, reason: r };
    sum = addDecimal(sum, v);
  }
  return { value: sum, reason: null };
}

interface Ctx {
  all: boolean;
  busy: string | null;
  editing: { id: string; draft: string } | null;
  editError: string | null;
  onRecompute: (l: LoanLine) => void;
  onEdit: (l: LoanLine) => void;
  onDraft: (v: string) => void;
  onSave: (l: LoanLine) => void;
  onCancel: () => void;
}

const normPrice = (v: string): string => v.trim().replace(',', '.');
const priceOk = (v: string): boolean => isDecimalString(normPrice(v)) && compareDecimal(normPrice(v), '0') > 0;

function PriceCell({ l, ctx }: { l: LoanLine; ctx: Ctx }) {
  const e = ctx.editing;
  if (e && e.id === l.event_id) {
    const ok = priceOk(e.draft);
    return (
      <span className="stack" style={{ gap: 4, alignItems: 'flex-end' }}>
        <span className="row-wrap" style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
          <input
            className="input mono" style={{ width: 120, minHeight: 32, textAlign: 'right' }} inputMode="decimal" value={e.draft} aria-invalid={!ok}
            data-testid={`price-input-${l.event_id}`} aria-label={`Új árszint: ${formatUtc(l.occurred_at)} UTC ${l.asset}`} onChange={(x) => ctx.onDraft(x.target.value)}
          />
          <button type="button" className="btn small primary" data-testid={`price-save-${l.event_id}`} disabled={!ok || ctx.busy !== null} onClick={() => ctx.onSave(l)}>Mentés</button>
          <button type="button" className="btn small" onClick={ctx.onCancel}>Mégse</button>
        </span>
        {!ok ? <span className="neg small" role="alert" data-testid="price-edit-error">Pozitív decimális szám kell.</span> : null}
        {ctx.editError ? <span className="neg small" role="alert">{ctx.editError}</span> : null}
      </span>
    );
  }
  return (
    <span className="stack" style={{ gap: 2, alignItems: 'flex-end' }}>
      <span className="mono">
        <Val value={l.price_level} reason={l.price_level_reason} dp={2} />
        {l.price_level !== null && l.price_level_asset && l.price_kind === 'stablecoin' ? <span className="muted"> {l.price_level_asset} (ref.)</span> : null}
        {l.price_level !== null ? <PriceSourceLabel source={l.price_level_source} /> : null}
      </span>
      <button
        type="button" className="btn small" data-testid={`price-edit-${l.event_id}`} disabled={ctx.busy !== null}
        aria-label={`Ár szerkesztése: ${formatUtc(l.occurred_at)} UTC ${l.asset}`} title="Az árszintet kézzel adod meg; a kézi ár elsőbbséget élvez (Candle → Binance nyilvános → kézi)"
        onClick={() => ctx.onEdit(l)}
      >
        Ár szerkesztése
      </button>
    </span>
  );
}

const resultClass = (v: string | null): string => (signClass(v) === 'neg' ? 'warn' : signClass(v));

function columns(ctx: Ctx): Column<LoanLine>[] {
  const cols: Column<LoanLine>[] = [
    { key: 'at', label: 'Felvéve (UTC)', help: 'openedAt', ariaLabel: 'felvétel ideje', sortValue: (l) => l.occurred_at, render: (l) => <span className="mono">{formatUtc(l.occurred_at)}</span> },
    { key: 'loc', label: 'Helyszín', sortValue: (l) => l.location, render: (l) => l.location },
    { key: 'qty', label: 'Felvett', numeric: true, help: 'quantity', ariaLabel: 'felvett mennyiség', sortValue: (l) => l.quantity, render: (l) => <span className="mono">{formatDecimal(l.quantity)} <b>{l.asset}</b></span> },
    { key: 'out', label: 'Kint lévő', numeric: true, help: 'loanOutstanding', ariaLabel: 'kint lévő mennyiség', sortValue: (l) => l.outstanding_quantity, render: (l) => <span className="mono">{formatDecimal(l.outstanding_quantity)} {l.asset}{l.status === 'repaid' ? <span className="pill" style={{ marginLeft: 6 }}>Visszafizetve</span> : null}</span> },
    { key: 'level', label: 'Felvételi ár', numeric: true, help: 'borrowPrice', ariaLabel: 'ár a felvételkor', sortValue: (l) => l.price_level, render: (l) => <PriceCell l={l} ctx={ctx} /> },
    { key: 'vab', label: 'Érték akkor, USD', numeric: true, help: 'valueAtBorrow', ariaLabel: 'érték felvételkor', sortValue: (l) => l.value_at_borrow_usd, render: (l) => <span className="mono"><Val value={l.value_at_borrow_usd} reason={l.value_at_borrow_reason} dp={2} /></span> },
    {
      key: 'cur', label: 'Mai ár', numeric: true, help: 'currentPrice', ariaLabel: 'mai ár', sortValue: (l) => l.current_price_usd,
      render: (l) => <span className="mono"><Val value={l.current_price_usd} reason={l.current_price_reason} dp={2} />{l.current_price_usd !== null ? <PriceSourceLabel source={l.current_price_source} /> : null}</span>,
    },
    { key: 'now', label: 'Érték ma, USD', numeric: true, help: 'valueNow', ariaLabel: 'érték ma', sortValue: (l) => l.value_now_usd, render: (l) => <span className="mono"><Val value={l.value_now_usd} reason={l.value_now_reason} dp={2} /></span> },
    {
      key: 'diff', label: 'Különbség, USD', numeric: true, help: 'repayCostChange', ariaLabel: 'különbség USD-ben',
      sortValue: (l) => l.change_usd,
      render: (l) => (l.change_usd === null ? <Dash reason={l.change_reason} /> : <span className={`mono ${l.verdict === 'worse' ? 'warn' : l.verdict === 'better' ? 'pos' : ''}`}>{formatDecimal(l.change_usd, { dp: 2, sign: true })}</span>),
    },
    {
      key: 'pct', label: 'Különbség, %', numeric: true, help: 'repayCostPct', ariaLabel: 'különbség százalékban',
      sortValue: (l) => l.change_pct,
      render: (l) => (l.change_pct === null ? <Dash reason={l.change_reason} /> : <span className={`mono ${l.verdict === 'worse' ? 'warn' : l.verdict === 'better' ? 'pos' : ''}`}>{formatPercent(l.change_pct)}</span>),
    },
  ];
  if (ctx.all) {
    cols.push(
      {
        key: 'state', label: 'Állapot', help: 'loanClosed', ariaLabel: 'állapot', sortValue: (l) => (isZeroDecimal(l.outstanding_quantity) ? (l.closed_at ?? l.occurred_at) : null),
        render: (l) =>
          isZeroDecimal(l.outstanding_quantity) ? (
            <span data-testid="loan-closed"><span className="pill">Lezárt</span>{l.closed_at ? <span className="mono small muted"> {formatUtc(l.closed_at)} UTC</span> : null}</span>
          ) : (
            <span className="muted" data-testid="loan-open">Nyitott</span>
          ),
      },
      {
        key: 'realized', label: 'Realizált eredmény, USD', numeric: true, help: 'realizedResult', ariaLabel: 'realizált eredmény kamattal',
        sortValue: (l) => l.realized_result_incl_interest_usd,
        render: (l) => (
          <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }} data-testid={`loan-realized-${l.event_id}`}>
            {l.realized_result_incl_interest_usd === null ? (
              <Dash reason={l.realized_result_incl_interest_reason ?? l.realized_result_reason} />
            ) : (
              <span className={`mono ${resultClass(l.realized_result_incl_interest_usd)}`}>{formatDecimal(l.realized_result_incl_interest_usd, { dp: 2, sign: true })}</span>
            )}
            {l.realized_result_usd !== null && l.realized_result_incl_interest_usd !== null ? <span className="muted small mono">kamat nélkül: {formatDecimal(l.realized_result_usd, { dp: 2, sign: true })}</span> : null}
          </span>
        ),
      },
      {
        key: 'interest', label: 'Fizetett kamat', numeric: true, help: 'interestPaid', ariaLabel: 'fizetett kamat', sortValue: (l) => l.interest_paid_quantity,
        render: (l) => (
          <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }} data-testid={`loan-interest-${l.event_id}`}>
            <span className="mono">{formatDecimal(l.interest_paid_quantity)} {l.asset}</span>
            {l.interest_paid_usd === null ? <span className="small"><Dash reason={l.interest_paid_reason} /></span> : <span className="muted small mono">≈ {formatDecimal(l.interest_paid_usd, { dp: 2 })} USD</span>}
          </span>
        ),
      },
    );
  }
  cols.push(
    { key: 'deal', label: 'Ügylet', help: 'deal', ariaLabel: 'ügylet', sortValue: (l) => l.deal_name, render: (l) => (l.deal_id === null ? <span className="muted">—</span> : <Link to={`/deals/${l.deal_id}`}>#{l.deal_id} {l.deal_name}</Link>) },
    {
      key: 'act', label: 'Művelet', srOnly: true,
      render: (l) =>
        l.can_recompute ? (
          <button type="button" className="btn small" disabled={ctx.busy !== null} aria-label={`Árszint újraszámítása: ${formatUtc(l.occurred_at)} ${l.asset}`} title="Árszint újraszámítása: a felvétel percének árát újra lekérdezi (Candle, majd Binance nyilvános)" onClick={() => ctx.onRecompute(l)}>
            {ctx.busy === l.event_id ? 'Lekérdezés…' : 'Újraszámítás'}
          </button>
        ) : null,
    },
  );
  return cols;
}

function unassignedColumns(g: UnassignedGroupView, onAssign: (r: UnassignedRepayView) => void, onPrehistory: (r: UnassignedRepayView) => void): Column<UnassignedRepayView>[] {
  return [
    { key: 'at', label: 'Visszafizetve (UTC)', sortValue: (r) => r.occurred_at, render: (r) => <span className="mono">{formatUtc(r.occurred_at)}</span> },
    { key: 'qty', label: 'Mennyiség', numeric: true, sortValue: (r) => r.quantity, render: (r) => <span className="mono">{formatDecimal(r.quantity)} {g.asset}</span> },
    { key: 'int', label: 'Ebből kamat', numeric: true, sortValue: (r) => r.interest_quantity, render: (r) => <span className="mono">{formatDecimal(r.interest_quantity)} {g.asset}</span> },
    { key: 'wait', label: 'Hozzárendelésre vár', numeric: true, help: 'unassignedRepay', ariaLabel: 'hozzárendelésre váró mennyiség', sortValue: (r) => r.unassigned_quantity, render: (r) => <b className="mono warn" data-testid="unassigned-quantity">{formatDecimal(r.unassigned_quantity)} {g.asset}</b> },
    { key: 'lvl', label: 'Ár a visszafizetéskor', numeric: true, sortValue: (r) => r.price_level, render: (r) => <span className="mono"><Val value={r.price_level} reason={r.price_level_reason} dp={2} /></span> },
    {
      key: 'act', label: 'Művelet', srOnly: true,
      render: (r) => (
        <span className="row-wrap" style={{ flexWrap: 'nowrap' }}>
          <button type="button" className="btn small primary" data-testid={`unassigned-assign-${r.repay_event_id}`} aria-label={`Hozzárendelés: ${formatUtc(r.occurred_at)} UTC ${formatDecimal(r.quantity)} ${g.asset}`} onClick={() => onAssign(r)}>
            Hozzárendelés
          </button>
          <button
            type="button" className="btn small" data-testid={`unassigned-prehistory-${r.repay_event_id}`} title={PREHISTORY_TITLE}
            aria-label={`Előzmény: ${formatUtc(r.occurred_at)} UTC ${formatDecimal(r.quantity)} ${g.asset}. ${PREHISTORY_TITLE}`} onClick={() => onPrehistory(r)}
          >
            Előzmény
          </button>
        </span>
      ),
    },
  ];
}

export function LoansPage() {
  const [status, setStatus] = useState<LoanQuery['status']>('open');
  const [locationId, setLocationId] = useState('');
  const [asset, setAsset] = useState('');
  const query: LoanQuery = { status, ...(locationId ? { location_id: locationId } : {}), ...(asset ? { asset } : {}) };
  const loans = useApi((s) => getLoanList(query, s), [status, locationId, asset]);
  // Filter choices come from the unfiltered list, so choosing one never hides the other options.
  const everything = useApi((s) => getLoanList({ status: 'all' }, s));
  const locs = useApi(getLocationItems);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const [stillMissing, setStillMissing] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; draft: string } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [forceFor, setForceFor] = useState<LoanLine | null>(null);
  const [assigning, setAssigning] = useState<{ group: UnassignedGroupView; repay: UnassignedRepayView } | null>(null);
  // D51: pre-history marks. One dialog at a time.
  const [pre, setPre] = useState<
    | { kind: 'mark'; group: UnassignedGroupView; repay: UnassignedRepayView }
    | { kind: 'note' | 'unmark'; group: PrehistoryGroupView; repay: PrehistoryRepayView }
    | null
  >(null);
  const [preOpen, setPreOpen] = useState(loadPrehistoryOpen);

  const assets = useMemo(() => [...new Set((everything.data?.items ?? []).map((l) => l.asset))].sort(), [everything.data]);
  const locations = useMemo(() => {
    const used = new Set((everything.data?.items ?? []).map((l) => l.location_id));
    return (locs.data ?? []).filter((l) => used.has(l.id));
  }, [everything.data, locs.data]);

  const reloadAll = () => {
    loans.reload();
    everything.reload();
  };

  const recompute = async (l: LoanLine, force: boolean) => {
    setBusy(l.event_id);
    setErr(null);
    setStillMissing(null);
    setFlash(null);
    try {
      const ev = await postRecomputePriceLevel(l.event_id, force);
      if (ev.price_level === null) setStillMissing(reasonText(ev.price_level_reason));
      setForceFor(null);
      reloadAll();
    } catch (x) {
      if (x instanceof ApiError && x.status === 409 && x.reason === 'manual_price_protected' && !force) setForceFor(l);
      else setErr(x instanceof Error ? x : new Error(String(x)));
    } finally {
      setBusy(null);
    }
  };

  const savePrice = async (l: LoanLine) => {
    if (!editing || !priceOk(editing.draft)) return;
    setBusy(l.event_id);
    setEditError(null);
    setErr(null);
    setFlash(null);
    try {
      await putPriceLevel(l.event_id, normPrice(editing.draft));
      setEditing(null);
      setFlash('Az árszint elmentve (forrás: kézi).');
      reloadAll();
    } catch (x) {
      setEditError(x instanceof ApiError ? reasonText(x.reason) : 'A mentés nem sikerült.');
    } finally {
      setBusy(null);
    }
  };

  const ctx: Ctx = {
    all: status === 'all', busy, editing, editError,
    onRecompute: (l) => void recompute(l, false),
    onEdit: (l) => { setEditError(null); setEditing({ id: l.event_id, draft: l.price_level ?? '' }); },
    onDraft: (v) => setEditing((e) => (e ? { ...e, draft: v } : e)),
    onSave: (l) => void savePrice(l),
    onCancel: () => { setEditing(null); setEditError(null); },
  };
  const closeAssign = useCallback(() => setAssigning(null), []);
  const closeForce = useCallback(() => setForceFor(null), []);
  const closePre = useCallback(() => setPre(null), []);
  const unassigned = loans.data?.unassigned ?? [];
  const prehistory = loans.data?.prehistory ?? [];
  const preDone = (msg: (ev: EventRow) => string) => (ev: EventRow) => {
    setPre(null);
    setErr(null);
    setFlash(msg(ev));
    reloadAll();
  };
  const togglePre = () => {
    const next = !preOpen;
    setPreOpen(next);
    storePrehistoryOpen(next);
  };

  return (
    <main className="page">
      <div className="page-head">
        <div className="titles">
          <h1>Hitelek</h1>
          <div className="muted">Hitelfelvételenként: mennyit ért akkor, mennyit ér ma, és mennyivel kerülne többe vagy kevesebbe most visszafizetni · USD, minden időpont UTC</div>
        </div>
        <Link className="btn" to="/loans/closed" data-testid="closed-loans-link">Lezárt hitelek</Link>
      </div>

      <section className="card" style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px 24px' }} aria-label="Szűrők">
        <div className="field">
          <HelpLabel helpKey="loanStatus" label="állapot" className="flabel">Állapot</HelpLabel>
          <div className="seg big" role="group" aria-label="Állapot">
            <button type="button" className={status === 'open' ? 'on' : ''} aria-pressed={status === 'open'} onClick={() => setStatus('open')}>Nyitott</button>
            <button type="button" className={status === 'all' ? 'on' : ''} aria-pressed={status === 'all'} onClick={() => setStatus('all')}>Mind</button>
          </div>
        </div>
        <div className="field">
          <label htmlFor="loan-loc">Helyszín</label>
          <select id="loan-loc" className="input" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">Mind</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="loan-asset">Coin</label>
          <select id="loan-asset" className="input" value={asset} onChange={(e) => setAsset(e.target.value)}>
            <option value="">Mind</option>
            {assets.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </section>

      {err ? <ErrorNotice error={err} /> : null}
      {flash ? <Notice kind="ok" title={flash} /> : null}
      {stillMissing ? <Notice kind="warn" title="Az ár még nem érhető el">{stillMissing} Próbáld újra később, vagy add meg kézzel.</Notice> : null}

      {unassigned.length > 0 ? (
        <section className="card warnb tight" aria-label="Hozzárendelésre váró visszafizetések" data-testid="unassigned-banner">
          <CardHead
            title="Hozzárendelésre vár"
            hint="Ezek a visszafizetések (vagy részeik) egyik hitelhez sincsenek kötve: a helyszín tartozását csökkentik, de a rendszer nem talál ki párosítást. Válaszd ki, melyik hitelt fizették vissza; ha a rendszer indulása előtti, nem rögzített hitelt, jelöld „Előzmény”-nek."
          />
          {unassigned.map((g) => (
            <div key={`${g.location_id}-${g.asset}`} className="stack" style={{ gap: 8 }} data-testid={`unassigned-group-${g.asset}`}>
              <div>
                <b>{g.location} · {g.asset}</b>: <span className="mono warn" data-testid="unassigned-total">{formatDecimal(g.unassigned_repay_quantity)} {g.asset}</span> hozzárendelésre vár
              </div>
              <Table
                columns={unassignedColumns(g, (r) => setAssigning({ group: g, repay: r }), (r) => setPre({ kind: 'mark', group: g, repay: r }))}
                rows={g.repays}
                rowKey={(r) => r.repay_event_id}
                dense
                defaultSort={{ key: 'at', dir: 'desc' }}
                label={`Hozzárendelésre váró visszafizetések: ${g.location} ${g.asset}`}
              />
            </div>
          ))}
        </section>
      ) : null}

      {prehistory.length > 0 ? (
        <PrehistorySection
          groups={prehistory} open={preOpen} onToggle={togglePre}
          onNote={(group, repay) => setPre({ kind: 'note', group, repay })} onUnmark={(group, repay) => setPre({ kind: 'unmark', group, repay })}
        />
      ) : null}

      <section className="card tight" aria-label="Hitelfelvételek">
        <CardHead title="Hitelfelvételek" hint="A visszafizetések tételesen vannak a felvételekhez kötve: a javaslat a legmagasabb felvételi árú hitelt fizeti vissza először, te átírhatod. A felvételi ár a törési pont; a kamat külön mennyiség. Ügylet nélkül is működik." />
        <Async state={loans}>
          {(data) => (
            <Table
              columns={columns(ctx)}
              rows={data.items}
              rowKey={(l) => l.event_id}
              dense
              defaultSort={{ key: 'at', dir: 'desc' }}
              rowClass={(l) => (l.verdict === 'worse' ? 'row-worse' : l.verdict === 'better' ? 'row-better' : undefined)}
              label="Hitelfelvételek"
              empty="Nincs a szűrőnek megfelelő hitelfelvétel."
              footer={
                data.items.length ? (
                  <tr>
                    <td colSpan={5}>Összesen (kint lévő részek, USD)</td>
                    <td colSpan={2} />
                    <td className="num mono"><Val {...asVal(total(data.items, (l) => ({ v: l.value_now_usd, r: l.value_now_reason })))} dp={2} /></td>
                    <TotalChange rows={data.items} />
                    <td colSpan={ctx.all ? 6 : 3} />
                  </tr>
                ) : undefined
              }
            />
          )}
        </Async>
      </section>

      {assigning ? (
        <Dialog title="Visszafizetés hozzárendelése" onClose={closeAssign}>
          <div data-testid="assign-dialog" className="stack" style={{ gap: 10 }}>
            <div className="muted small">
              {assigning.group.location} · {formatUtc(assigning.repay.occurred_at)} UTC · {formatDecimal(assigning.repay.quantity)} {assigning.group.asset}
              {' '}(ebből kamat: {formatDecimal(assigning.repay.interest_quantity)} {assigning.group.asset})
            </div>
            <RepayPanel
              mode="assign" locationId={assigning.group.location_id} locationName={assigning.group.location} asset={assigning.group.asset}
              quantity={assigning.repay.quantity} interest={assigning.repay.interest_quantity} occurredAt={assigning.repay.occurred_at} repayEventId={assigning.repay.repay_event_id}
              onSaved={(r) => {
                setAssigning(null);
                setFlash(`A visszafizetés hozzárendelve a kiválasztott hitelekhez.${r.interestChanged ? ` A kamat-rész most ${formatDecimal(r.interest)} ${assigning.group.asset}.` : ''}`);
                reloadAll();
              }}
            />
            <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={closeAssign}>Bezárás</button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {pre?.kind === 'mark' ? (
        <PrehistoryMarkDialog
          target={{ location: pre.group.location, asset: pre.group.asset, repay: pre.repay }} unassigned={pre.repay.unassigned_quantity} onClose={closePre} onFailed={reloadAll}
          onDone={preDone((ev) => `Előzményként megjelölve: ${formatDecimal(ev.prehistory?.quantity ?? pre.repay.unassigned_quantity)} ${pre.group.asset}. A „Hozzárendelésre vár” listából kikerült; a jelölés bármikor visszavonható.`)}
        />
      ) : null}
      {pre?.kind === 'note' ? (
        <PrehistoryNoteDialog
          target={{ location: pre.group.location, asset: pre.group.asset, repay: pre.repay }} note={pre.repay.note} onClose={closePre} onFailed={reloadAll}
          onDone={preDone(() => 'Az előzmény megjegyzése elmentve.')}
        />
      ) : null}
      {pre?.kind === 'unmark' ? (
        <PrehistoryUnmarkDialog
          target={{ location: pre.group.location, asset: pre.group.asset, repay: pre.repay }} onClose={closePre} onFailed={reloadAll}
          onDone={preDone(() => 'Az előzmény-jelölés visszavonva; a visszafizetés újra hozzárendelésre vár.')}
        />
      ) : null}

      {forceFor ? (
        <Dialog title="Kézi ár felülírása" onClose={closeForce}>
          <div data-testid="force-confirm" className="stack" style={{ gap: 10 }}>
            <p style={{ margin: 0 }}>
              A(z) {formatUtc(forceFor.occurred_at)} UTC-kor felvett {formatDecimal(forceFor.quantity)} {forceFor.asset} árszintét kézzel adtad meg ({forceFor.price_level !== null ? formatDecimal(forceFor.price_level, { dp: 2 }) : '—'}).
              Az újraszámítás ezt a kézi árat <b>felülírja</b> a Candle (vagy a Binance nyilvános) árával. Biztosan folytatod?
            </p>
            <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn" data-testid="force-confirm-no" onClick={closeForce}>Mégse</button>
              <button type="button" className="btn primary" data-testid="force-confirm-yes" disabled={busy !== null} onClick={() => void recompute(forceFor, true)}>Igen, felülírom</button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </main>
  );
}

const asVal = (t: { value: string | null; reason: string | null }) => ({ value: t.value, reason: t.reason });

function TotalChange({ rows }: { rows: LoanLine[] }) {
  const t = total(rows, (l) => ({ v: l.change_usd, r: l.change_reason }));
  return (
    <td className={`num mono ${signClass(t.value) === 'pos' ? 'warn' : signClass(t.value) === 'neg' ? 'pos' : ''}`}>
      {t.value === null ? <Dash reason={t.reason} /> : formatDecimal(t.value, { dp: 2, sign: true })}
    </td>
  );
}

